/**
 * Unified query key definitions for React Query.
 * All query keys must be defined here — no scattered declarations.
 */
export const queryKeys = {
  // Documents
  documents: ['documents'] as const,
  document: (id: string) => ['documents', id] as const,

  // P4 multimodal-vision (SP-3) — matter-scoped read endpoint
  // GET /api/v1/matters/{matterId}/documents/{docId}/vision
  documentVision: (matterId: string, docId: string) =>
    ['documents', docId, 'vision', matterId] as const,

  // Validation
  validation: (matterId?: string | null) =>
    matterId ? (['validation', matterId] as const) : (['validation'] as const),

  // Reports
  report: (id: string) => ['reports', id] as const,

  // Classification / Amendments
  amendmentConsolidated: (baseDocId: string) =>
    ['amendments', baseDocId, 'consolidated'] as const,
  amendmentDiffs: (baseDocId: string) =>
    ['amendments', baseDocId, 'diffs'] as const,

  // Jurisdiction
  jurisdictionRules: (jurisdiction?: string, docType?: string) =>
    ['jurisdiction', 'rules', jurisdiction, docType] as const,
  jurisdictionCoverage: ['jurisdiction', 'coverage'] as const,
  jurisdictionValidation: (documentId: string) =>
    ['jurisdiction', 'validation', documentId] as const,
  jurisdictionUserSettings: ['jurisdiction', 'user-settings'] as const,

  // Redactions — matter-scoped (backend exposes only
  // /api/v1/matters/{matterId}/documents/{docId}/redactions per ADR-082).
  redactions: (matterId: string, docId: string, orgId: string) =>
    ['redactions', matterId, docId, orgId] as const,

  // Intake
  intakeState: (matterId: string) =>
    ['intake', matterId, 'checklist-state'] as const,

  // Practices — global reference list of launch practice areas (NORTH-STAR §3).
  // Required for the matter-create practice picker (#3133 seam).
  practices: ['practices'] as const,

  // Matters
  // matters() without arg = all-matters key (legacy, avoids breaking other callers)
  // matters(status) = status-scoped key for MatterListPage filter chip wiring
  matters: (status?: 'active' | 'archived' | 'trashed' | 'all') =>
    status ? (['matters', status] as const) : (['matters'] as const),

  // Matter-scoped document list — MatterDetail Documents tab
  // Scoped under ['matter', matterId, 'documents'] so React Query
  // invalidates correctly alongside ['matter', matterId] (parent).
  matterDocuments: (matterId: string) =>
    ['matter', matterId, 'documents'] as const,

  // Matter-scoped activity log — MatterDetail Activity tab (Pkg #1 FE)
  // Key includes filters object so filter changes trigger separate queries.
  // AC-2: key = ['matter', matterId, 'activity', filters]
  matterActivity: (matterId: string, filters?: object) =>
    ['matter', matterId, 'activity', filters ?? {}] as const,

  // Admin
  adminUsers: ['admin', 'users'] as const,
  adminInvites: ['admin', 'invites'] as const,

  // Sources
  sources: ['sources'] as const,
  source: (id: string) => ['sources', id] as const,
  monitoringStatus: (id: string) => ['sources', id, 'status'] as const,
  cloudFolders: (sourceId: string, path: string) =>
    ['sources', sourceId, 'folders', path] as const,
  cloudFiles: (sourceId: string, folderId: string) =>
    ['sources', sourceId, 'files', folderId] as const,
  watchedFolders: ['sources', 'watched-folders'] as const,
  syncHistory: (sourceId: string, documentId?: string) =>
    ['sources', sourceId, 'sync-history', documentId] as const,

  // Teams
  teams: (matterId: string) => ['teams', matterId] as const,
  team: (matterId: string, teamId: string) =>
    ['teams', matterId, teamId] as const,
  auditLog: (params?: object) => ['audit-log', params] as const,

  // Encryption
  encryptionStatus: ['encryption', 'status'] as const,
  encryptionAuditLog: (params?: object) =>
    ['encryption-audit-log', params] as const,

  // BYOK (Enterprise)
  byokStatus: ['byok', 'status'] as const,

  // Attestation (Enterprise)
  attestationReports: ['attestation', 'reports'] as const,
  attestationReport: (id: string) => ['attestation', 'reports', id] as const,

  // Auditor (Enterprise)
  auditorStatus: ['auditor', 'status'] as const,
  auditorAuditLog: (params?: object) =>
    ['auditor', 'audit-log', params] as const,

  // Dashboard
  dashboardStats: ['dashboard', 'stats'] as const,
  dashboardTaxonomy: ['dashboard', 'taxonomy'] as const,
  dashboardConfidence: ['dashboard', 'confidence'] as const,
  dashboardQualityIssues: ['dashboard', 'quality-issues'] as const,
  dashboardRiskRates: ['dashboard', 'risk-rates'] as const,

  // Compliance reports
  complianceReports: ['compliance', 'reports'] as const,
  complianceReport: (id: string) => ['compliance', 'reports', id] as const,

  // Findings (human-in-loop review)
  findings: (status?: string) => ['findings', status] as const,
  finding: (id: string) => ['findings', id] as const,

  // Privilege Review (attorney-client privilege workflow)
  // Parent key for blanket invalidation after any privilege mutation.
  privilegeReview: ['privilege-review'] as const,
  privilegeReviewQueue: (params?: object) =>
    ['privilege-review', 'queue', params ?? {}] as const,
  privilegeAuditTrail: (documentId?: string) =>
    ['privilege-review', 'audit-trail', documentId ?? null] as const,
  privilegeLog: ['privilege-review', 'log'] as const,

  // Tenant settings
  tenantSettings: ['tenant', 'settings'] as const,

  // Branding
  branding: ['branding'] as const,
  brandingReport: ['branding', 'report'] as const,

  // TI Costs (admin)
  tiCosts: (days: number) => ['admin', 'ti-costs', days] as const,

  // API Keys
  apiKeys: ['api-keys'] as const,

  // Usage Metering
  usageCurrent: ['usage', 'current'] as const,
  usageSummary: (start: string, end: string, keyId?: string) =>
    ['usage', 'summary', start, end, keyId] as const,

  // Folders
  folders: (parentId?: string) =>
    parentId ? (['folders', parentId] as const) : (['folders'] as const),
  folder: (id: string) => ['folders', id] as const,

  // Baselines
  baselines: ['baselines'] as const,
  baseline: (id: string) => ['baselines', id] as const,

  // Scheduled Reports
  reportSchedules: ['report-schedules'] as const,

  // Email Monitors
  emailMonitors: ['email-monitors'] as const,
  matterThreads: (matterId: string) => ['email-threads', matterId] as const,

  // VDR (Virtual Data Room)
  vdrConnections: ['vdr', 'connections'] as const,
  vdrWorkspaces: (provider: string, pageToken?: string) =>
    ['vdr', provider, 'workspaces', pageToken] as const,
  vdrDocuments: (provider: string, workspaceId: string, folderPath?: string) =>
    ['vdr', provider, 'documents', workspaceId, folderPath] as const,
  vdrSyncState: (provider: string, workspaceId: string) =>
    ['vdr', provider, 'sync', workspaceId] as const,
  vdrAudit: (
    provider: string,
    workspaceId?: string,
    limit?: number,
    offset?: number,
  ) => ['vdr', provider, 'audit', workspaceId, limit, offset] as const,

  // RAG Search
  ragSearch: ['rag', 'search'] as const,
  ragResults: (query: string) => ['rag', 'results', query] as const,

  // Agent Query
  agentDryRun: (query: string) => ['agent', 'dry-run', query] as const,
  agentQuery: (query: string) => ['agent', 'query', query] as const,
} as const;
